import { useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import {
    type FindStoreResult,
    type ProductStoreStockViewModel,
    SearchStoreByAddress,
    SearchStoreByCoordinates
} from 'autogen/swagger/all';
import { formatReact } from 'modules/helpers/strings';
import QuickView, { useQuickView } from 'components/quickView';
import type { SearchRequestBody } from 'components/QuickViewSearch/shared/types';
import { breakpoint, sizes } from 'variables';
import {
    OnGeoLocationCallbackContext,
    OnSearchCallbackContext,
    OnSelectParcelPickupContext
} from 'components/storeList/shared/helpers';
import StoreListQuickView from 'components/storeList';
import { GA4Tracking } from 'modules/tracking/GA4/GA4Tracking';
import InlineButton from 'components/generic/InlineButton';
import UAEventTracking from 'modules/tracking/UAEventTracking';
import { Typography } from 'components/generic/typography';
import { ColorBrandBlue5, ColorNeutralGrey5, FontLineHeightNormal, Spacing8 } from 'autogen/design-tokens/tokens';
// eslint-disable-next-line camelcase
import { useGetProductStoreStockModelQuery, use_GetSelectedProductQuery } from 'autogen/swagger/all/hooks';
import { DefaultQuickViewHeader } from 'components/quickView/components/DefaultQuickViewComponents/Header';
import AbsoluteQuickViewHeader from 'components/quickView/components/QuickViewWithAbsoluteHeader/Header';
import ContentLoader from 'react-content-loader';
import useThemeHandler from './themes/useThemeHandler';
import Bullet from 'components/Bullet';

const Container = styled(Typography.Body)`
    margin-bottom: 0;
    line-height: ${FontLineHeightNormal};
`;

const StyledQuickView = styled(QuickView)`
    ${breakpoint.up(sizes.md)} {
        min-width: initial;
    }
`;

interface IStore {
    model: ProductStoreStockViewModel;
}

interface IStockButton {
    buttonText: ProductStoreStockViewModel['buttonText'];
    request: ProductStoreStockViewModel['request'];
    findStoreResult?: FindStoreResult;
    initialPreferredStore: ProductStoreStockViewModel['initialPreferredStore'];
}

function StockButton({ buttonText, request, findStoreResult, initialPreferredStore }: IStockButton): JSX.Element {
    const { productId } = request;
    const { toggle } = useQuickView('storeStock');

    const {
        data: selectedProduct,
        isFetching,
        refetch: getSelectedProduct
    } = use_GetSelectedProductQuery(
        { productId },
        {
            enabled: false,
            refetchOnMount: false,
            refetchOnWindowFocus: false
        }
    );

    const isLoading = isFetching || !selectedProduct;

    function onClickHandler() {
        if (selectedProduct) {
            toggle(true);

            UAEventTracking({
                eventCategory: 'productPage',
                eventAction: 'openStoreStockStatus'
            });

            GA4Tracking({
                eventName: 'store_stock_status',
                context: 'product_page',
                category: 'ecommerce'
            });
        } else {
            toggle(true);
            getSelectedProduct();
        }
    }

    const { ButtonStyling } = useThemeHandler() as any;

    return (
        <ThemeProvider theme={{ ButtonStyling }}>
            <InlineButton type="button" onClick={onClickHandler}>
                {buttonText}
            </InlineButton>

            <StyledQuickView
                quickViewId="storeStock"
                trackingCategory="storeStockCheckerQuickView"
                position="right"
                headerRenderer={() =>
                    selectedProduct?.image ? (
                        <AbsoluteQuickViewHeader userCanClose onClose={() => toggle(false)} />
                    ) : (
                        <DefaultQuickViewHeader userCanClose onClose={() => toggle(false)} />
                    )
                }
            >
                {isLoading ? (
                    <ContentLoader
                        speed={2}
                        width="100%"
                        height="100%"
                        viewBox="0 0 400 460"
                        preserveAspectRatio="none"
                        backgroundColor={ColorNeutralGrey5}
                        foregroundColor={ColorBrandBlue5}
                    >
                        <rect x="0" y="0" rx="2" ry="2" width="100%" height="100%" />
                    </ContentLoader>
                ) : (
                    <StoreListQuickView
                        findStoreResult={findStoreResult}
                        initialPreferredStore={initialPreferredStore}
                        showSelectParcelShop
                        productInfo={selectedProduct}
                    />
                )}
            </StyledQuickView>
        </ThemeProvider>
    );
}

export default function StoreStock({ model: initialModelProductStoreStockModel }: IStore): JSX.Element {
    const { request: initialRequest } = initialModelProductStoreStockModel;

    const { data: productStoreStockModel, refetch: getProductStoreStockModel } = useGetProductStoreStockModelQuery(
        initialRequest, // Not sure if this needs to always be the initial request or we should use the request that comes from the productStoreStockModel that we fetch
        { initialData: initialModelProductStoreStockModel, enabled: false }
    );
    const { checkMark, request, text, buttonText, initialPreferredStore } =
        productStoreStockModel || initialModelProductStoreStockModel;

    const [findStoreResult, setFindStoreResult] = useState<FindStoreResult | undefined>(undefined);

    const { productId } = request;
    const onlyIfSundays = false;
    const { toggle } = useQuickView('storeStock');

    function tracking(addressQuery) {
        GA4Tracking({
            eventName: 'find_in_store',
            category: 'ecommerce',
            context: 'product_page',
            eventParams: [{ param_name: 'store', param_value: addressQuery }]
        });
    }

    function onSearchCallback(data: SearchRequestBody): void {
        const { searchQuery: addressQuery } = data;
        SearchStoreByAddress({ addressQuery, productId, onlyIfSundays }).then((response) => {
            setFindStoreResult(response);
            tracking(response.searchData.addressQuery);
        });
    }

    function onGeoLocationCallback(latitude: number, longitude: number): void {
        SearchStoreByCoordinates({ latitude, longitude, productId, onlyIfSundays }).then((response) => {
            setFindStoreResult(response);
            tracking(response.searchData.addressQuery);
        });
    }

    function onSelectParcelPickup(): void {
        getProductStoreStockModel().then(() => toggle(false));
    }

    return (
        <OnSearchCallbackContext.Provider value={onSearchCallback}>
            <OnGeoLocationCallbackContext.Provider value={onGeoLocationCallback}>
                <OnSelectParcelPickupContext.Provider value={onSelectParcelPickup}>
                    <Container size="S">
                        {formatReact(text)(
                            <StockButton
                                buttonText={buttonText}
                                findStoreResult={findStoreResult}
                                initialPreferredStore={initialPreferredStore}
                                request={request}
                            />
                        )}
                    </Container>
                </OnSelectParcelPickupContext.Provider>
            </OnGeoLocationCallbackContext.Provider>
        </OnSearchCallbackContext.Provider>
    );
}
